import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Placeholder } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { ActionButton } from 'app/components/Button/ActionButton';
import InputSearch from 'app/components/Input/InputSearch';
import ShowImageDefault from 'app/components/ShowImage/ShowImageDefault';
import { QUERIES } from 'app/components/Table/core/constants';
import { ID } from 'app/components/Table/core/types';
import Typography from 'app/components/Typography';

import { useAuth } from 'app/modules/auth';
import { SensorStatus } from 'app/modules/inventory/bins/components/SensorStatus';
import { DISPLAY_SENSOR_SKU_ID } from 'app/modules/inventory/bins/core/constant';
import { SensorConnectionStatus, SensorSkuId } from 'app/modules/inventory/bins/core/enum';
import { BinDetailCardWrapper, CardInfo } from 'app/modules/inventory/bins/core/styles';
import { UnableToRemoveItemModal } from 'app/modules/inventory/bins/modal/UnableToRemoveItem.modal';
import { getRack, updateBinInRack } from 'app/modules/inventory/racks/core/requests';
import { useRackStore } from 'app/modules/inventory/racks/store';
import { getExpirySensors, getSensors } from 'app/modules/system-settings/devices/core/requests';

import { injectDragToScroll } from 'app/helpers/utils';
import { useBoolean } from 'app/hooks';
import { usePolicyInUser } from 'app/hooks/policy';
import { cloneDeep, orderBy } from 'lodash';

import { ReactComponent as IconBack } from 'app/assets/icons/icon-back.svg';

import LabelStockTypeCard from '../items/components/LabelStockTypeCard';
import { getItems } from '../items/core/requests';
import { ActionBin } from '../racks/core/enum';
import { UpdateBinsRequest } from '../racks/core/type';
import BinDetailCard from './components/BinDetailCard';
import { Bin } from './core/type';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const heightBin = 136;
const removeBinContainer = 'remove-bin';

const Main = styled.div`
  width: 100%;
  padding-top: 28px;
`;

const AssignBinsContainer = styled.div<{
  $isEditMode: boolean;
}>`
  width: ${(props) => (props.$isEditMode ? '350px' : '0%')};
  flex-shrink: 0;
  background: #f2f4f7;
  border-radius: 8px;
  padding: ${(props) => (props.$isEditMode ? '0px 0px 16px' : '0')};
  overflow-y: auto;
  transition: all 0.3s ease-in-out;
  visibility: ${(props) => (props.$isEditMode ? 'visible' : 'hidden')};
`;

const BinLayoutContainer = styled.div<{ $isEditMode: boolean }>`
  padding: ${(props) => (props.$isEditMode ? '16px' : '0')};
  background: ${(props) => (props.$isEditMode ? '#f2f4f7' : 'transparent')};
  width: 100%;
  border-radius: 8px;
  transition: all 0.4s ease-in-out;
  height: calc(100vh - 312px);
  overflow-y: auto;
  flex: 1 1 0;
`;

export const GridView = styled.div<{ $totalColumns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$totalColumns}, 1fr);
  gap: 12px;
  padding: 28px;
  overflow-x: auto;
  user-select: none;
`;

export const StatusSensorWrapper = styled.div<{ $isSensorActive: boolean }>`
  height: 14px;
  width: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--success-light-2);
  background: ${({ $isSensorActive }) =>
    $isSensorActive ? 'var(--success-light-2)' : 'var(--danger-light-2)'};
`;

export const StatusSensor = styled.div<{ $isSensorActive: boolean }>`
  width: 6px;
  height: 6px;
  background: var(--success);
  background: ${({ $isSensorActive }) => ($isSensorActive ? 'var(--success)' : 'var(--danger)')};
  border-radius: 50%;
`;

const TabsContainer = styled.div`
  display: flex;
  background: #f5f7fa;
  border-bottom: 1px solid var(--neutral-4);
  border-radius: 8px 8px 0 0;
`;

const Tab = styled.div<{ active: boolean; tab: string }>`
  flex: 1;
  text-align: center;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
  color: ${(props) => (props.active ? '#007bff' : '#9b9b9b')};
  padding: 12px 0;
  cursor: pointer;
  position: relative;

  &:after {
    content: '';
    display: ${(props) => (props.active ? 'block' : 'none')};
    height: 3px;
    width: ${(props) => (props.tab !== 'expired-sensors' ? '50%' : '100%')};
    background: #007bff;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 3px;
  }
`;

const ButtonBack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: 1px solid var(--neutral-4);
  padding: 6px 12px;
  border-radius: 8px;

  &:hover {
    border-color: var(--primary);
    p {
      color: var(--primary);
    }

    svg {
      path {
        stroke: var(--primary);
      }
    }
  }
`;

const ScrollableListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  gap: 12px;
  display: flex;
  flex-direction: column;
`;

const ItemSkeleton = styled.div`
  display: flex;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  min-height: 92px;
  gap: 12px;
  align-items: flex-start;
  flex-direction: column;
`;

const SensorSkeleton = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  min-height: 56px;
  gap: 12px;
`;

interface SensorConfig {
  dragRef: React.MutableRefObject<string | null>;
  sensorsKey: 'sensors' | 'expirySensors';
  sensorKey: 'sensor' | 'expirySensor';
  sensors: any[];
  hasQtyCheck?: boolean;
}

interface Props {
  bins: Bin[];
  onClickModal: (key: ActionBin, originBin: Bin) => void;
  inSession?: boolean;
  totalBins: number;
  totalColumns: number;
  totalRows: number;
  rackId: ID;
  fetchBins: () => void;
  onBack: () => void;
  isRackCustom?: boolean;
}
const ViewListBinDrag: React.FC<Props> = ({
  bins,
  onClickModal,
  inSession,
  totalBins,
  totalColumns,
  totalRows,
  rackId,
  fetchBins,
  onBack,
  isRackCustom,
}) => {
  const isLoadingSave = useBoolean(false);
  const unableToRemoveItem = useBoolean(false);

  const [data, setData] = useState<Bin[]>(bins);
  const [draggedOverBin, setDraggedOverBin] = useState<number | string | null>(null);

  const dragItem = useRef<number | null>(null);
  const dragSensor = useRef<number | null>(null);
  const dragExpirySensor = useRef<number | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('items');
  const [originalData, setOriginalData] = useState<Bin[]>(bins);

  const sensors = useRackStore((state) => state.state.sensors);
  const itemsInBin = useRackStore((state) => state.state.items);
  const expirySensors = useRackStore((state) => state.state.expirySensors);

  const gridViewRef = useRef<HTMLDivElement | null>(null);

  const queryClient = useQueryClient();

  const permission = usePolicyInUser(useAuth().currentUser?.policy ?? []);
  const isSystemAdminCurrent = permission.systemAdmin;
  const organization = useOrganizationStore((state) => state.state.organization);

  const params = useParams<{ rackId: string; orgId: string }>();
  const orgId = params.orgId ? Number(params.orgId) : organization?.id;

  injectDragToScroll('bin-layout');

  const heightScrollBinLayout =
    gridViewRef.current?.scrollWidth ?? 0 > (gridViewRef.current?.clientWidth ?? 0) ? 8 : 0;

  const hasChanges = useMemo(() => {
    return data.some((bin) => {
      const originalBin = originalData.find((item) => item.id === bin.id);
      const isChanged =
        bin.item?.id !== originalBin?.item?.id ||
        bin.sensor?.id !== originalBin?.sensor?.id ||
        bin.expirySensor?.id !== originalBin?.expirySensor?.id;

      return originalBin ? isChanged : true;
    });
  }, [data, originalData]);

  useEffect(() => {
    const formatBins = bins.map((bi) => ({
      ...bi,
      row: bi.row === 0 ? null : bi.row,
      col: bi.col === 0 ? null : bi.col,
    }));
    setData(formatBins);
    setOriginalData(formatBins);
  }, [JSON.stringify(bins)]);

  useEffect(() => {
    return () => {
      queryClient.clear();
    };
  }, []);

  const { isLoading: isLoadingItems } = useQuery({
    queryKey: ['items'],
    queryFn: () =>
      getItems(
        `page=1&limit=999999999&orderBy=createdAt&orderDirection=DESC&status=1${
          isSystemAdminCurrent ? `&orgId=${organization?.id}` : ''
        }`,
      ),
    onSuccess: (itemsRes) => {
      useRackStore.getState().setState({ items: itemsRes?.data?.items ?? [] });
    },
    staleTime: 60 * 1000,
    enabled: activeTab === 'items',
  });

  const { isLoading: isLoadingSensors } = useQuery({
    queryKey: ['sensors'],
    queryFn: () =>
      getSensors(
        `page=1&limit=999999999&orderBy=createdAt&orderDirection=DESC${
          isSystemAdminCurrent ? `&orgId=${organization?.id}` : ''
        }&type=unassigned`,
      ),
    onSuccess: (sensorsRes) => {
      const counterSensors = sensorsRes?.data?.items?.filter(
        (sensor) => sensor.skuId !== SensorSkuId.Expiry,
      );

      useRackStore.getState().setState({ sensors: counterSensors ?? [] });
    },
    staleTime: 60 * 1000,
    enabled: activeTab === 'sensors',
  });

  const { isLoading: isLoadingExpirySensors } = useQuery({
    queryKey: [QUERIES.EXPIRY_SENSOR_LIST],
    queryFn: () =>
      getExpirySensors(
        `page=1&limit=999999999&orderBy=createdAt&orderDirection=DESC${
          isSystemAdminCurrent ? `&orgId=${organization?.id}` : ''
        }&status=unassigned`,
      ),
    onSuccess: (expirySensorsRes) => {
      useRackStore.getState().setState({ expirySensors: expirySensorsRes?.data?.items ?? [] });
    },
    staleTime: 60 * 1000,
    enabled: activeTab === 'expired-sensors',
  });

  const filteredItems = useMemo(() => {
    return itemsInBin
      ?.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.skuId?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      ?.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
      );
  }, [searchTerm, itemsInBin]);

  const filteredSensors = useMemo(() => {
    return sensors
      ?.filter(
        (sensor) =>
          sensor.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sensor.skuId?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      ?.sort((a, b) =>
        a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' }),
      );
  }, [searchTerm, sensors]);

  const filteredExpirySensors = useMemo(() => {
    return expirySensors
      ?.filter((sensor) => sensor.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      ?.sort(
        (a, b) =>
          a?.name?.localeCompare(b?.name || '', undefined, {
            numeric: true,
            sensitivity: 'base',
          }) ?? 0,
      );
  }, [searchTerm, expirySensors]);

  async function handleSortItems() {
    if (dragItem.current === null || draggedOverBin === null) return;
    const itemsClone = cloneDeep(itemsInBin);
    const binsClone = cloneDeep(data);

    const dragIndex = itemsClone.findIndex((ele) => ele.id === dragItem.current);
    if (dragIndex === -1) return;

    const draggedItem = itemsClone[dragIndex];

    const targetIndex = binsClone.findIndex((ele) => ele.id === draggedOverBin);
    const targetBin = binsClone[targetIndex];

    if (targetBin?.item?.qty && targetBin?.item?.qty > 0) {
      unableToRemoveItem.setValue(true);
      setDraggedOverBin(null);
      dragItem.current = null;
      return;
    }

    if (targetBin?.id) {
      binsClone[targetIndex] = {
        ...targetBin,
        item: draggedItem,
      };
    }

    setData(binsClone);

    setDraggedOverBin(null);
    dragItem.current = null;
  }

  const handleSensorSort = (config: SensorConfig) => {
    const { dragRef, sensorsKey, sensorKey, sensors: sensorsList, hasQtyCheck = false } = config;

    if (!dragRef.current || !draggedOverBin) return;

    const [sensorsClone, binsClone] = [cloneDeep(sensorsList), cloneDeep(data)];

    // Find dragged sensor
    const dragIndex = sensorsClone.findIndex((sensor) => sensor.id === dragRef.current);
    if (dragIndex === -1) return;

    const draggedSensor = sensorsClone[dragIndex];
    const targetIndex = binsClone.findIndex((bin) => bin.id === draggedOverBin);
    const targetBin = binsClone[targetIndex];

    if (sensorKey === 'sensor' && hasQtyCheck && targetBin?.item?.qty && targetBin?.item?.qty > 0) {
      unableToRemoveItem.setValue(true);
      resetDragState();
      return;
    }

    // Get original sensor for potential swap
    const originalSensor = targetBin?.[sensorKey];

    // Update target bin
    if (targetBin?.id) {
      binsClone[targetIndex] = {
        ...targetBin,
        [sensorKey]: draggedSensor,
      };
    }

    // Update bins data
    setData(orderBy(binsClone, ['row', 'col']));

    // Update sensors list
    const updatedSensors = [
      ...sensorsClone.filter((sensor) => sensor.id !== draggedSensor.id),
      ...(originalSensor ? [originalSensor] : []),
    ];

    // Update store
    useRackStore.getState().setState({ [sensorsKey]: updatedSensors });

    resetDragState();
  };

  const resetDragState = () => {
    setDraggedOverBin(null);
    dragSensor.current = null;
    dragExpirySensor.current = null;
  };

  const handleSortSensors = () =>
    handleSensorSort({
      dragRef: dragSensor as any,
      sensorsKey: 'sensors',
      sensorKey: 'sensor',
      sensors,
      hasQtyCheck: true,
    });

  const handleSortExpirySensors = () =>
    handleSensorSort({
      dragRef: dragExpirySensor as any,
      sensorsKey: 'expirySensors',
      sensorKey: 'expirySensor',
      sensors: expirySensors,
      hasQtyCheck: true,
    });

  const handleDragEnter = (id: number) => {
    setDraggedOverBin(id);
  };

  const handleUpdateBins = async () => {
    isLoadingSave.setValue(true);

    try {
      const body: UpdateBinsRequest[] = data.map((bin) => ({
        binId: Number(bin.id),
        ...(bin.item?.id ? { itemId: Number(bin.item.id) } : {}),
        ...(bin.sensor?.id ? { sensorId: Number(bin.sensor.id) } : {}),
        ...(bin.expirySensor?.id ? { expirySensorId: Number(bin.expirySensor.id) } : {}),
      }));

      const res = await updateBinInRack(rackId, body);

      if (res.success) {
        await fetchBins();
        const rack = await getRack(rackId, orgId);
        useRackStore.getState().setState({ rack });
        toast.success(`Update bins successfully!`);
      }
    } finally {
      isLoadingSave.setValue(false);
    }
  };

  const handleCancelChange = () => {
    setData(originalData);
    queryClient.invalidateQueries(['sensors']);
    queryClient.invalidateQueries([QUERIES.EXPIRY_SENSOR_LIST]);
  };

  const handleRemoveItemOrSensor = async (binId: ID, itemId: ID, sensorId: ID) => {
    const cloneData = cloneDeep(data);

    if (itemId) {
      const targetIndex = cloneData.findIndex((el) => el.id === binId);

      if (targetIndex !== -1) {
        cloneData[targetIndex] = {
          ...cloneData[targetIndex],
          item: null,
        };
      }
    }

    if (sensorId) {
      const targetIndex = cloneData.findIndex((el) => el.id === binId);
      const removedSensor = cloneData[targetIndex].sensor;

      if (targetIndex !== -1) {
        cloneData[targetIndex] = {
          ...cloneData[targetIndex],
          sensor: null,
        };

        if (removedSensor) {
          useRackStore.getState().setState({
            sensors: [...sensors, removedSensor],
          });
        }
      }
    }

    setData(cloneData);
  };

  const handleRemoveExpirySensor = (binId: ID) => {
    const cloneData = cloneDeep(data);
    const targetIndex = cloneData.findIndex((el) => el.id === binId);

    if (targetIndex !== -1) {
      const removedSensor = cloneData[targetIndex].expirySensor;

      cloneData[targetIndex] = {
        ...cloneData[targetIndex],
        expirySensor: null,
      };

      if (removedSensor) {
        useRackStore.getState().setState({ expirySensors: [...expirySensors, removedSensor] });
      }

      setData(cloneData);
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleChangeTab = (tab: string) => {
    setActiveTab(tab);
    setSearchTerm('');
  };

  const emptyPositions: { row: number; col: number }[] = [];
  for (let row = 1; row <= totalRows; row++) {
    for (let col = 1; col <= totalColumns; col++) {
      const binExists = bins.some((bin) => bin.row === row && bin.col === col);
      if (!binExists) {
        emptyPositions.push({ row, col });
      }
    }
  }

  const isShowSkeleton = isLoadingItems || isLoadingSensors || isLoadingExpirySensors;

  const isShowItems = !isLoadingItems && activeTab === 'items' && filteredItems?.length > 0;

  const isShowSensors = !isLoadingSensors && activeTab === 'sensors' && filteredSensors?.length > 0;

  const isShowExpirySensors =
    !isLoadingExpirySensors && activeTab === 'expired-sensors' && filteredExpirySensors?.length > 0;

  const isShowNoItems =
    !isLoadingItems && activeTab === 'items' && filteredItems.length === 0 && searchTerm;

  const isShowNoSensors =
    !isLoadingSensors && activeTab === 'sensors' && filteredSensors.length === 0 && searchTerm;

  const isShowNoExpirySensors =
    !isLoadingExpirySensors &&
    activeTab === 'expired-sensors' &&
    filteredExpirySensors.length === 0;

  return (
    <>
      <Main className="d-flex flex-column">
        <div
          style={{ padding: '0 28px', gap: '48px', minHeight: '43px' }}
          className="d-flex align-items-center justify-content-between"
        >
          <ButtonBack onClick={onBack}>
            <IconBack />
            <Typography variant="body-3" color="neutral-10">
              Back
            </Typography>
          </ButtonBack>

          <ActionButton
            notMarginTop
            onCancel={handleCancelChange}
            labelButtonSubmit="Save layout"
            themeButton="btn-primary"
            onSubmit={handleUpdateBins}
            isSubmitting={isLoadingSave.value}
            disableButton={!hasChanges || isLoadingSave.value}
            isShowButtonReset={hasChanges}
          />
        </div>

        <div className="d-flex" style={{ padding: '16px 27px 28px 28px', gap: 28 }}>
          <AssignBinsContainer
            $isEditMode={true}
            draggable={false}
            style={{
              opacity: draggedOverBin === removeBinContainer ? 0.4 : 1,
              height: 'calc(100vh - 312px)',
            }}
            className="d-flex flex-column"
          >
            <TabsContainer>
              <Tab
                active={activeTab === 'items'}
                tab="items"
                onClick={() => handleChangeTab('items')}
              >
                Items
              </Tab>
              <Tab
                active={activeTab === 'sensors'}
                tab="sensors"
                onClick={() => handleChangeTab('sensors')}
              >
                Sensors
              </Tab>
              <Tab
                active={activeTab === 'expired-sensors'}
                onClick={() => handleChangeTab('expired-sensors')}
                tab="expired-sensors"
              >
                Expired Sensors
              </Tab>
            </TabsContainer>

            <div
              className="d-flex flex-column"
              style={{ padding: '16px', gap: 12, flex: 1, overflow: 'hidden' }}
            >
              <InputSearch
                styleMain={{
                  width: '100%',
                  background: '#FFFFFF',
                  fontSize: 13,
                }}
                value={searchTerm}
                onChange={onInputChange}
                placeholder={
                  activeTab === 'items'
                    ? 'Search with SKU or item name'
                    : activeTab === 'sensors'
                    ? 'Search with sensor ID'
                    : 'Search with expiry sensor name'
                }
              />

              <ScrollableListContainer>
                {isShowSkeleton &&
                  (activeTab === 'items'
                    ? Array.from({ length: 6 }).map((_, index) => (
                        <ItemSkeleton key={`item-skeleton-${index}`}>
                          <div className="d-flex flex-column w-100">
                            <div className="d-flex">
                              <div
                                style={{
                                  minWidth: 32,
                                  minHeight: 32,
                                  width: 32,
                                  height: 32,
                                  background: '#f0f0f0',
                                  borderRadius: '4px',
                                }}
                              ></div>
                              <div
                                className="d-flex flex-column"
                                style={{ marginLeft: 8, flexGrow: 1, width: 'calc(100% - 40px)' }}
                              >
                                <Placeholder as="div" animation="wave">
                                  <Placeholder xs={7} style={{ height: '18px' }} />
                                </Placeholder>
                                <Placeholder as="div" animation="wave" className="mt-1">
                                  <Placeholder xs={4} style={{ height: '14px' }} />
                                </Placeholder>
                                <Placeholder as="div" animation="wave" className="mt-1">
                                  <Placeholder xs={5} style={{ height: '14px' }} />
                                </Placeholder>
                              </div>
                            </div>
                          </div>
                        </ItemSkeleton>
                      ))
                    : Array.from({ length: 10 }).map((_, index) => (
                        <SensorSkeleton key={`sensor-skeleton-${index}`}>
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: '#f0f0f0',
                            }}
                          ></div>
                          <div
                            className="d-flex flex-column"
                            style={{ width: 'calc(100% - 40px)' }}
                          >
                            <Placeholder as="div" animation="wave">
                              <Placeholder xs={6} style={{ height: '16px' }} />
                            </Placeholder>
                            <Placeholder as="div" animation="wave" className="mt-1">
                              <Placeholder xs={4} style={{ height: '12px' }} />
                            </Placeholder>
                          </div>
                        </SensorSkeleton>
                      )))}

                {isShowItems &&
                  filteredItems?.map((item) => (
                    <CardInfo
                      key={item.id}
                      draggable
                      onDragStart={() => (dragItem.current = item.id as number)}
                      onDragEnd={handleSortItems}
                    >
                      <div className="d-flex flex-column w-100">
                        <div className="d-flex">
                          <ShowImageDefault
                            image={item.image ?? ''}
                            styleMain={{
                              minWidth: 32,
                              minHeight: 32,
                              width: 32,
                              height: 32,
                            }}
                          />
                          <div
                            className="d-flex flex-column"
                            style={{
                              marginLeft: 8,
                              flexGrow: 1,
                              width: 'calc(100% - 40px)',
                            }}
                          >
                            <Typography variant="body-2" className="text-overflow-ellipsis">
                              {item.name}
                            </Typography>
                            <LabelStockTypeCard stockType={Number(item.stockType)} />
                            <Typography
                              variant="body-3"
                              color="neutral-7"
                              style={{
                                whiteSpace: 'nowrap',
                                marginTop: 1,
                              }}
                            >
                              {item.skuId}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </CardInfo>
                  ))}

                {isShowSensors &&
                  filteredSensors?.map((sensor) => (
                    <CardInfo
                      key={sensor.id}
                      className="flex-row min-h-55px"
                      draggable
                      onDragStart={() => (dragSensor.current = sensor.id as number)}
                      onDragEnd={handleSortSensors}
                    >
                      <SensorStatus status={SensorConnectionStatus.notReady} skuId={sensor.skuId} />
                      <div className="d-flex flex-column" style={{ width: 'calc(100% - 40px)' }}>
                        <Typography
                          className="text-ellipsis w-100"
                          variant="body-3"
                          color="neutral-10"
                          fontSize={16}
                          lineHeight={20}
                        >
                          {sensor.code}
                        </Typography>
                        <Typography
                          variant="body-5"
                          color="neutral-7"
                          fontSize={12}
                          lineHeight={20}
                        >
                          {DISPLAY_SENSOR_SKU_ID(sensor.skuId as SensorSkuId)}
                        </Typography>
                      </div>
                    </CardInfo>
                  ))}

                {isShowExpirySensors &&
                  filteredExpirySensors?.map((sensor) => (
                    <CardInfo
                      key={sensor.id}
                      className="flex-row min-h-55px"
                      draggable
                      onDragStart={() => (dragExpirySensor.current = sensor.id as number)}
                      onDragEnd={handleSortExpirySensors}
                    >
                      <SensorStatus
                        status={SensorConnectionStatus.notReady}
                        skuId={SensorSkuId.Expiry}
                      />
                      <div className="d-flex flex-column" style={{ width: 'calc(100% - 40px)' }}>
                        <Typography
                          className="text-ellipsis w-100"
                          variant="body-3"
                          color="neutral-10"
                          fontSize={16}
                          lineHeight={20}
                        >
                          {sensor.name}
                        </Typography>
                        <Typography
                          variant="body-5"
                          color="neutral-7"
                          fontSize={12}
                          lineHeight={20}
                        >
                          {DISPLAY_SENSOR_SKU_ID(SensorSkuId.Expiry)}
                        </Typography>
                      </div>
                    </CardInfo>
                  ))}

                {isShowNoItems && (
                  <Typography variant="body-3" className="text-center py-3">
                    No items {searchTerm ? `found matching "${searchTerm}"` : ''}
                  </Typography>
                )}

                {isShowNoSensors && (
                  <Typography variant="body-3" className="text-center py-3">
                    No sensors {searchTerm ? `found matching "${searchTerm}"` : ''}
                  </Typography>
                )}

                {isShowNoExpirySensors && (
                  <Typography variant="body-3" className="text-center py-3">
                    No expiry sensors {searchTerm ? `found matching "${searchTerm}"` : ''}
                  </Typography>
                )}
              </ScrollableListContainer>
            </div>
          </AssignBinsContainer>

          <BinLayoutContainer $isEditMode={true}>
            <GridView
              id="bin-layout"
              style={{
                padding: 0,
              }}
              $totalColumns={totalColumns}
              ref={gridViewRef}
            >
              {new Array(totalRows).fill(null).map((_el, rowIndex) => {
                const newBinsFilterRow: Bin[] = data.filter((el) => el.row === rowIndex + 1);
                const orderedBins = orderBy(newBinsFilterRow, 'col');

                return (
                  <React.Fragment key={rowIndex}>
                    {new Array(totalColumns).fill(null).map((_colEl, colIndex) => {
                      const bin = orderedBins.find((b) => b.col === colIndex + 1);
                      const isEmptyPosition = emptyPositions.some(
                        (pos) => pos.row === rowIndex + 1 && pos.col === colIndex + 1,
                      );

                      return (
                        <BinDetailCardWrapper
                          key={`${rowIndex}-${colIndex}`}
                          onDragEnter={() => bin && handleDragEnter(bin.id as number)}
                          onDragOver={(e) => {
                            e.preventDefault();
                          }}
                          isDraggedOverBin={draggedOverBin === bin?.id}
                          isEmptyPosition={isEmptyPosition}
                        >
                          {bin && !isEmptyPosition && (
                            <BinDetailCard
                              bin={bin}
                              onClickModal={onClickModal}
                              inSession={inSession}
                              isDraggedOver={draggedOverBin === bin.id}
                              isEditLayout
                              handleRemoveItemOrSensor={handleRemoveItemOrSensor}
                              onRemoveExpirySensor={handleRemoveExpirySensor}
                            />
                          )}
                        </BinDetailCardWrapper>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </GridView>
          </BinLayoutContainer>
        </div>

        {unableToRemoveItem.value && (
          <UnableToRemoveItemModal
            onClose={() => unableToRemoveItem.setValue(false)}
            show={unableToRemoveItem.value}
          />
        )}
      </Main>
    </>
  );
};

export default ViewListBinDrag;
